import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import InvoiceClient from '../../api/invoiceClient';
import { usePlanLimitContent } from '../help/planLimitContent';

const PAGE_SIZE = 25;

// Same accumulating "load more" fetch pattern as the other Step 4/5 pages.
// All the action handlers (issue/delete/paid/void/refresh/pdf/labels) are
// copied from the old page unchanged — same InvoiceClient calls, same 409
// conflict handling, same processing_issue "3 strikes → needs manual
// review" heuristic and processingIssueChecks/manualReviewInvoices state
// (CHANGES.md §6.2 — polling and the manual-review flag are preserved
// exactly, only the row's buttons are reorganised).
const useInvoicesPage = () => {
  const { t, i18n } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const invoiceClient = useMemo(() => (config?.backendUrl ? new InvoiceClient(config.backendUrl) : null), [config]);
  const planLimit = usePlanLimitContent('invoices.issued.monthly');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [invoiceContacts, setInvoiceContacts] = useState([]);
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [userList, setUserList] = useState([]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogInitialError, setCreateDialogInitialError] = useState('');
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);

  const [detailInvoice, setDetailInvoice] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [revisionInvoice, setRevisionInvoice] = useState(null);

  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, invoice: null });
  const [actionPending, setActionPending] = useState(false);
  const [processingIssueChecks, setProcessingIssueChecks] = useState({});
  const [manualReviewInvoices, setManualReviewInvoices] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Post-issue toast (design_handoff_invoice_actions/README.md): only for
  // invoices the user issued *this session* — tracked here and cleared once
  // the toast (or its mobile fallback) has been shown for that id, so a
  // later unrelated refresh of the same invoice never re-triggers it.
  const issuedIdsRef = useRef(new Set());
  const [actionToast, setActionToast] = useState({ open: false, invoice: null });
  const closeActionToast = () => setActionToast((s) => ({ ...s, open: false }));

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!config?.backendUrl || !companyId) return;
    axios.get(`${config.backendUrl}/users?companyId=${companyId}`)
      .then((res) => setUserList(res.data))
      .catch((error) => console.error('Error fetching users', error));
  }, [config, companyId]);

  const fetchInvoiceContacts = useCallback(() => {
    if (!invoiceClient) return Promise.resolve();
    return invoiceClient.getInvoiceContacts(companyId)
      .then((response) => setInvoiceContacts(response.data))
      .catch((error) => {
        console.error('Error fetching invoice contacts', error);
        throw error;
      });
  }, [invoiceClient, companyId]);

  useEffect(() => { fetchInvoiceContacts().catch(() => undefined); }, [fetchInvoiceContacts]);

  const fetchNoteTemplates = useCallback(() => {
    if (!invoiceClient) return Promise.resolve();
    return invoiceClient.getInvoiceNoteTemplates(companyId)
      .then((response) => setNoteTemplates(Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        console.error('Error fetching invoice note templates', error);
        throw error;
      });
  }, [invoiceClient, companyId]);

  useEffect(() => { fetchNoteTemplates().catch(() => undefined); }, [fetchNoteTemplates]);

  const fetchInvoices = useCallback((reset = true, trackProcessingIssues = false) => {
    if (!invoiceClient || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const filters = { page, pageSize: PAGE_SIZE };
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (search) filters.search = search;
    invoiceClient.getInvoices(companyId, filters)
      .then((response) => {
        const items = response.data.items || [];
        setRows((prev) => (reset ? items : [...prev, ...items]));
        setTotal(response.data.total ?? items.length);
        setStatus('ready');
        if (trackProcessingIssues) {
          setProcessingIssueChecks((current) => {
            const next = { ...current };
            items.forEach((invoice) => {
              if (invoice.status === 'processing_issue') next[invoice._id] = (next[invoice._id] || 0) + 1;
              else delete next[invoice._id];
            });
            return next;
          });
          setManualReviewInvoices((current) => {
            const next = { ...current };
            items.forEach((invoice) => { if (invoice.status !== 'processing_issue') delete next[invoice._id]; });
            return next;
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching invoices', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /invoices · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [invoiceClient, companyId, statusFilter, search, t]);

  useEffect(() => { fetchInvoices(true); }, [fetchInvoices]);

  const handleRefresh = () => fetchInvoices(true, true);
  const loadMore = () => fetchInvoices(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  // ---- Create / edit -----------------------------------------------------//
  const handleOpenCreate = () => {
    setCreateDialogInitialError('');
    setEditInvoice(null);
    setCreateDialogOpen(true);
  };
  const handleOpenEdit = (invoice) => {
    setCreateDialogInitialError('');
    setEditInvoice(invoice);
    setCreateDialogOpen(true);
  };
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setEditInvoice(null);
    setRevisionInvoice(null);
  };
  const handleSaved = (message) => {
    setCreateDialogInitialError('');
    notify(message);
    handleCloseCreateDialog();
    fetchInvoices();
    fetchInvoiceContacts().catch(() => undefined);
  };

  const handleOpenDetail = (invoice) => {
    setDetailInvoice(invoice);
    setDetailDialogOpen(true);
  };
  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setDetailInvoice(null);
  };

  const handleOpenConfirm = (type, invoice) => setConfirmDialog({ open: true, type, invoice });
  const handleCloseConfirm = () => setConfirmDialog({ open: false, type: null, invoice: null });

  const applyFreshInvoice = (freshInvoice, manualReview = false) => {
    if (!freshInvoice?._id) return;
    setRows((current) => current.map((item) => (item._id === freshInvoice._id ? freshInvoice : item)));
    setDetailInvoice((current) => (current?._id === freshInvoice._id ? freshInvoice : current));
    setProcessingIssueChecks((current) => {
      const next = { ...current };
      if (freshInvoice.status === 'processing_issue') next[freshInvoice._id] = (next[freshInvoice._id] || 0) + 1;
      else delete next[freshInvoice._id];
      return next;
    });
    if (manualReview) {
      setManualReviewInvoices((current) => ({ ...current, [freshInvoice._id]: true }));
    } else if (freshInvoice.status !== 'processing_issue') {
      setManualReviewInvoices((current) => {
        const next = { ...current };
        delete next[freshInvoice._id];
        return next;
      });
    }

    // The invoice the user issued this session has now reached `issued`
    // (immediately on the happy path, or later once a processing_issue row
    // is refreshed) — show the one-click Email Invoice/View toast instead of
    // (or, off desktop, alongside) the plain success text.
    if (freshInvoice.status === 'issued' && issuedIdsRef.current.has(freshInvoice._id)) {
      issuedIdsRef.current.delete(freshInvoice._id);
      if (isDesktop) {
        setActionToast({ open: true, invoice: freshInvoice });
      } else {
        notify(t('Invoice issued successfully.'));
      }
    }
  };

  // ---- Revision (issued custom invoices only) ----------------------------//
  // Reuses the create dialog's full edit form in revise mode.
  const handleOpenRevision = (invoice) => {
    setCreateDialogInitialError('');
    setEditInvoice(null);
    setRevisionInvoice(invoice);
    setDetailDialogOpen(false);
    setCreateDialogOpen(true);
  };

  // ---- Send email (issued invoices) --------------------------------------//
  const handleOpenEmail = (invoice) => {
    setEmailInvoice(invoice);
    setEmailDialogOpen(true);
  };
  const handleCloseEmail = () => {
    setEmailDialogOpen(false);
    setEmailInvoice(null);
  };
  const handleEmailSent = (freshInvoice) => {
    handleCloseEmail();
    applyFreshInvoice(freshInvoice);
    notify(t('Invoice email sent.', { defaultValue: 'Invoice email sent.' }));
  };

  const handleActionToastSendEmail = () => {
    const { invoice } = actionToast;
    closeActionToast();
    if (invoice) handleOpenEmail(invoice);
  };
  const handleActionToastView = () => {
    const { invoice } = actionToast;
    closeActionToast();
    if (invoice) handleOpenDetail(invoice);
  };

  const handleRemind = async (invoice) => {
    if (!invoiceClient || !invoice) return;
    try {
      const response = await invoiceClient.remindInvoice(invoice._id);
      applyFreshInvoice(response.data);
      notify(t('Reminder sent to the recipient.', { defaultValue: 'Reminder sent to the recipient.' }));
    } catch (error) {
      console.error('Error sending invoice reminder', error);
      notify(error.response?.data?.error || t('Something went wrong'), 'error');
    }
  };

  const handleRefreshInvoice = async (invoiceId) => {
    if (!invoiceClient || !invoiceId) return;
    try {
      const response = await invoiceClient.getInvoice(invoiceId);
      applyFreshInvoice(response.data);
    } catch (error) {
      console.error('Error refreshing invoice', error);
      notify(error.response?.data?.error || t('Error refreshing invoice.'), 'error');
    }
  };

  const handleConfirmAction = async () => {
    if (actionPending) return;
    const { type, invoice } = confirmDialog;
    if (!invoice || !invoiceClient) return;

    setActionPending(true);
    try {
      if (type === 'issue') {
        const response = await invoiceClient.issueInvoice(invoice._id);
        issuedIdsRef.current.add(invoice._id);
        applyFreshInvoice(response.data);
      } else if (type === 'delete') {
        await invoiceClient.deleteInvoice(invoice._id);
        try {
          const res = await invoiceClient.getInvoice(invoice._id);
          notify(res?.data?._id ? t('Deletion was not confirmed. The invoice was refreshed.') : t('Invoice deleted successfully.'), res?.data?._id ? 'warning' : 'success');
        } catch (getError) {
          notify(t('Invoice deleted successfully.'));
        }
      } else if (type === 'paid') {
        await invoiceClient.setInvoiceStatus(invoice._id, 'paid');
        notify(t('Invoice marked as paid. Source orders were updated.'));
      } else if (type === 'void') {
        await invoiceClient.setInvoiceStatus(invoice._id, 'void');
        notify(t('Invoice voided successfully.'));
      }
      handleCloseConfirm();
      setDetailDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      console.error(`Error performing ${type} on invoice`, error);
      const respStatus = error.response?.status;
      const message = error.response?.data?.error || t('Something went wrong');

      if (type === 'issue' && respStatus === 409) {
        let freshInvoice;
        try {
          const response = await invoiceClient.getInvoice(invoice._id);
          freshInvoice = response.data;
          applyFreshInvoice(freshInvoice, message === 'Invoice issue rollback failed');
        } catch (refreshError) {
          console.error('Error refreshing invoice after issue conflict', refreshError);
        }
        if (freshInvoice?.status === 'issued') {
          notify(t('The invoice was already issued. Its latest state has been loaded.'), 'info');
        } else if (freshInvoice?.status === 'processing_issue') {
          notify(
            message === 'Invoice issue rollback failed'
              ? t('This invoice needs manual review. Issuing, editing, and deleting are unavailable until backend support resolves it.')
              : t('This invoice is still being issued. Wait a moment and refresh before taking another action.'),
            message === 'Invoice issue rollback failed' ? 'error' : 'warning'
          );
        } else {
          notify(message, 'error');
        }
      } else if (type === 'issue' && respStatus === 400 && message.startsWith('Insufficient inventory for ')) {
        await invoiceClient.getInventoryItems(companyId).catch((inventoryError) => {
          console.error('Error refreshing inventory after issue failure', inventoryError);
        });
        let freshInvoice = invoice;
        try {
          const response = await invoiceClient.getInvoice(invoice._id);
          freshInvoice = response.data;
          applyFreshInvoice(freshInvoice);
        } catch (refreshError) {
          console.error('Error refreshing invoice after inventory failure', refreshError);
        }
        setCreateDialogInitialError(message);
        setEditInvoice(freshInvoice);
        setCreateDialogOpen(true);
        setDetailDialogOpen(false);
      } else {
        if (respStatus === 409) {
          try {
            const response = await invoiceClient.getInvoice(invoice._id);
            applyFreshInvoice(response.data);
          } catch (refreshError) {
            console.error('Error refreshing invoice after conflict', refreshError);
          }
        }
        notify(message, 'error');
      }
      handleCloseConfirm();
    } finally {
      setActionPending(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    if (!invoiceClient) return;
    try {
      const res = await invoiceClient.downloadPdf(invoice._id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF', error);
      notify(t('Error downloading PDF.'), 'error');
    }
  };

  const handlePrintShippingLabel = async (invoice) => {
    if (!invoiceClient) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      notify(t('Error printing shipping label.'), 'error');
      return;
    }
    printWindow.opener = null;
    try {
      const res = await invoiceClient.getShippingLabel(invoice._id);
      const url = URL.createObjectURL(res.data);
      printWindow.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      printWindow.close();
      console.error('Error printing shipping label', error);
      notify(t('Error printing shipping label.'), 'error');
    }
  };

  const handleDownloadShippingLabel = async (invoice) => {
    if (!invoiceClient) return;
    let url;
    try {
      const res = await invoiceClient.getShippingLabel(invoice._id, true);
      url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}-shipping-label.pdf`;
      a.click();
    } catch (error) {
      console.error('Error downloading shipping label', error);
      notify(t('Error downloading shipping label.'), 'error');
    } finally {
      if (url) URL.revokeObjectURL(url);
    }
  };

  const confirmDialogText = () => {
    switch (confirmDialog.type) {
      case 'issue': return t('Are you sure you want to issue this invoice? This will assign an invoice number, generate the PDF, and deduct inventory-backed line quantities from stock.');
      case 'delete': return confirmDialog.invoice?.status === 'issued'
        ? t('Are you sure you want to delete this invoice? It will no longer be visible to the customer, any inventory it deducted will be returned to stock, and its source orders (if any) will no longer show as invoiced. This action cannot be undone.')
        : t('Are you sure you want to delete this draft invoice? This action cannot be undone.');
      case 'paid': return t('Are you sure you want to mark this invoice as paid?');
      case 'void': return t('Are you sure you want to void this invoice? Voiding does not return items to inventory.');
      default: return '';
    }
  };

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search || statusFilter !== 'all');

  return {
    t, i18n, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch, statusFilter, setStatusFilter,
    loadingMore, loadMore, remaining, nextBatchSize, handleRefresh,
    userList, invoiceContacts, fetchInvoiceContacts, noteTemplates, fetchNoteTemplates, invoiceClient,
    createDialogOpen, createDialogInitialError, editInvoice, handleOpenCreate, handleOpenEdit, handleCloseCreateDialog, handleSaved,
    contactsDialogOpen, setContactsDialogOpen,
    detailInvoice, detailDialogOpen, handleOpenDetail, handleCloseDetail,
    revisionInvoice, handleOpenRevision, handleRemind,
    emailInvoice, emailDialogOpen, handleOpenEmail, handleCloseEmail, handleEmailSent,
    confirmDialog, handleOpenConfirm, handleCloseConfirm, handleConfirmAction, actionPending, confirmDialogText,
    processingIssueChecks, manualReviewInvoices, handleRefreshInvoice,
    handleDownloadPdf, handlePrintShippingLabel, handleDownloadShippingLabel,
    snackbar, setSnackbar, fetchInvoices,
    actionToast, closeActionToast, handleActionToastSendEmail, handleActionToastView,
    planLimit,
  };
};

export default useInvoicesPage;
